import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { ConversionExistsService } from '../services/mbrs/conversion-exists.service';
import { SheetLoadTraceService } from '../services/mbrs/sheet-load-trace.service';
import { resolveDiagnosticScopeFromUrl } from '../utils/navigation-diagnostic-scope.util';
import { XBRL_FILES_URL } from '../constants/route-paths';

/**
 * Refuses a filing page for a conversion that does not exist or is not the
 * caller's, BEFORE the page mounts (P.O., 2026-10-14).
 *
 * Registered on `/mbrs/filing` as `canActivate`, after `isAuthenticatedGuard`
 * has already confirmed the session on the same navigation. Sheet switches
 * inside the page are query-param moves, which do not re-run `canActivate`
 * under the default `runGuardsAndResolvers`, so the check costs one small
 * request per filing OPEN and nothing per sheet.
 *
 * ## What each verdict does
 *
 * | Verdict | Result |
 * |---|---|
 * | `exists` | proceed |
 * | `inconclusive` | proceed — the check failing is not evidence about the conversion; the page's own request reports its own error |
 * | `not-found` (`RESP`) / `forbidden` (`PERM`) | refuse, and toast |
 *
 * ## Refuse = stay put, unless there is nowhere to stay
 *
 * - **A click inside the app** (XBRL Files, Dashboard): returning `false`
 *   leaves the user on the page they clicked from, with the toast. This is the
 *   behaviour asked for — no navigation at all.
 * - **A cold load** (typed URL, bookmark, refresh, new tab, the upload-success
 *   link): there is no page to stay on, and a bare `false` would cancel the
 *   only navigation and render a white page (the same trap `isAdminGuard`
 *   documents). The guard returns a {@link RedirectCommand} to the XBRL Files
 *   listing instead — the same destination the container's own post-load
 *   redirect uses, minus the filing page mounting first.
 *
 * The toast goes through {@link SheetLoadTraceService.reportPreNavigationFailure}
 * so it is rendered by the always-mounted shell (it survives both outcomes) and
 * so the production flag (`refactoredDiagnosticCodesEnabled` off) shows the
 * previous generic sentence rather than the coded one.
 *
 * The container's own `RESP`/`PERM` handling stays as the fallback for a
 * conversion removed between this check and the load.
 */
export const conversionExistsGuard: CanActivateFn = async (route, state) => {
  const router = inject(Router);
  const conversionExists = inject(ConversionExistsService);
  const loadTrace = inject(SheetLoadTraceService);

  // No id means a brand-new filing (Phase 1 starts without a conversion) —
  // there is nothing to look up.
  const conversionId = route.queryParamMap.get('id');
  if (!conversionId) {
    return true;
  }

  const result = await firstValueFrom(conversionExists.check(conversionId));

  if (result.verdict === 'exists' || result.verdict === 'inconclusive') {
    return true;
  }

  const failure = result.verdict === 'not-found' ? 'RESP' : 'PERM';
  // The target URL names the sheet being opened (`?sheet=`), so the code reads
  // exactly as the container's own would: `MBRS-S<role>-RESP-<id>`.
  const diagnosticScope = resolveDiagnosticScopeFromUrl(state.url);
  loadTrace.reportPreNavigationFailure(
    failure,
    diagnosticScope?.scope ?? 'S',
    conversionId,
    { httpStatus: result.httpStatus, errorCode: result.errorCode },
  );

  // `router.navigated` is false only for the very first navigation of the
  // document — i.e. a cold load with no page to return to.
  return router.navigated ? false : new RedirectCommand(router.parseUrl(XBRL_FILES_URL));
};
